// Customer portal — navigation tabbar + mobile drawer.
//
// Source-of-truth (legacy): /var/www/kitchenos/customer-standalone.html
//   .tabbar / .tab markup at lines 569-577
//   `navTo(page)` switcher at /var/www/kitchenos/customer-live.js:162
//
// Two presentations, single tab list:
//
//   • Desktop (≥1024px): horizontal sticky tabbar under the header.
//   • Tablet / mobile (<1024px): hamburger button in the header opens a
//     full-width drawer that slides down below the header. The drawer
//     items are 48px-tall touch targets, highlight the active page in
//     the retailer's brand colour, and may show count badges (e.g.
//     unread inbound messages on the Messages tab). Tapping an item
//     closes the drawer; tapping the backdrop closes it; Escape closes
//     it.
//
// Cross-section navigation (e.g. "Message your designer" → Messages
// page) is a `window` CustomEvent `cust-nav` with `detail.page`. Any
// section can dispatch it; only the shell listens.

import type { CustomerProject } from "../types";

export interface NavTab {
  /** Section id (must match a registered `CustomerSection.id`). */
  id: string;
  /** Label shown in the tabbar. */
  label: string;
  /** Inline SVG `<path>`/`<polyline>` content for the tab icon. The
   *  caller wraps these in a 24×24 viewBox with stroke=currentColor. */
  icon: string;
}

/** The menu items per Steve's 2026-05-19 spec, in the order they
 *  appear in the tabbar. Section ids on the right map to existing /
 *  newly-built customer sections. */
export const NAV_TABS: ReadonlyArray<NavTab> = [
  {
    id: "project-overview",
    label: "Home",
    icon: '<rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/>',
  },
  {
    // The kitchen journey moved to a progress bar on Home (Bug 4), so this
    // tab now opens Designs (Bug 5). Icon: stacked design sheets.
    id: "designs",
    label: "Designs",
    icon: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>',
  },
  {
    id: "quote",
    label: "Quote",
    icon: '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
  },
  {
    id: "payments",
    label: "Payments",
    icon: '<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>',
  },
  {
    id: "tracking",
    label: "Order & delivery",
    icon: '<rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
  },
  {
    id: "documents",
    label: "Documents",
    icon: '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>',
  },
  {
    id: "bookings",
    label: "Bookings",
    icon: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  },
  {
    id: "messages",
    label: "Messages",
    icon: '<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>',
  },
  {
    id: "review-prompt",
    label: "Reviews",
    icon: '<polygon points="12 2 15 9 22 9 17 14 19 21 12 17 5 21 7 14 2 9 9 9 12 2"/>',
  },
  {
    id: "account",
    label: "Account",
    icon: '<circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0112 0v1"/>',
  },
];

/** Section ids that have a tab in the nav (set form, for fast lookup). */
export const NAV_SECTION_IDS = new Set(NAV_TABS.map((t) => t.id));

/** Custom event name dispatched by a section that wants to navigate. */
export const NAV_EVENT_NAME = "cust-nav" as const;

export interface NavEventDetail {
  page: string;
}

/** Helper: dispatch a navigation event from anywhere in the portal. */
export function requestNav(page: string): void {
  window.dispatchEvent(
    new CustomEvent<NavEventDetail>(NAV_EVENT_NAME, { detail: { page } }),
  );
}

/** Read the active page from the URL hash. Accepts `#home`, `#/home`,
 *  `#page=home`, or `#token=...&page=home` — robust to whatever the
 *  rest of the portal puts in the hash. Returns null on miss. */
export function readPageFromHash(): string | null {
  const raw = window.location.hash.replace(/^#/, "").replace(/^\//, "");
  if (!raw) return null;
  if (!raw.includes("=") && !raw.includes("&") && !raw.includes("?")) {
    return raw;
  }
  const qs = raw.includes("?") ? raw.slice(raw.indexOf("?") + 1) : raw;
  try {
    const params = new URLSearchParams(qs);
    const p = params.get("page");
    if (p && p.trim() !== "") return p.trim();
  } catch {
    /* ignore */
  }
  return null;
}

/** Update the URL hash to reflect the active page. Preserves any
 *  pre-existing query params in the hash (e.g. the magic-link token
 *  that lives in `#token=...`). */
export function writePageToHash(page: string): void {
  const hash = window.location.hash.replace(/^#/, "");
  if (hash.includes("=") || hash.includes("&")) {
    const params = new URLSearchParams(
      hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : hash,
    );
    params.set("page", page);
    const prefix = hash.includes("?") ? hash.slice(0, hash.indexOf("?")) : "";
    window.history.replaceState(null, "", `#${prefix}?${params.toString()}`);
  } else {
    window.history.replaceState(null, "", `#${page}`);
  }
}

// ── Badges ──────────────────────────────────────────────────────────────────

/** Per-section badge count (e.g. unread inbound messages → 2). */
export type NavBadges = Partial<Record<string, number>>;

/** Compute badge counts from a project. Currently:
 *  - messages: unread inbound message count
 *  Future: payments overdue, documents-since-last-visit, etc. */
export function computeBadges(project: CustomerProject): NavBadges {
  // The authoritative unread count is computed server-side from the unified
  // messaging threads and surfaced as `project.unreadMessages`. The legacy
  // embedded `project.messages` array is almost always empty for real
  // customers, so counting it returned 0 even when messages had arrived — the
  // badge bug. Prefer the server count; fall back to the embedded array only
  // when the server field is absent (older payloads / demo project).
  const serverUnread =
    typeof project.unreadMessages === "number" ? project.unreadMessages : null;
  const legacyUnread = project.messages.filter(
    (m) => m.direction === "inbound" && !m.read,
  ).length;
  const unreadMessages = serverUnread ?? legacyUnread;
  return {
    messages: unreadMessages > 0 ? unreadMessages : undefined,
  };
}

function badgeHtml(count: number | undefined): string {
  if (!count || count <= 0) return "";
  const display = count > 99 ? "99+" : String(count);
  return `<span class="cust-nav-badge" aria-label="${count} new">${display}</span>`;
}

// ── Desktop tabbar ──────────────────────────────────────────────────────────

/** Build the desktop tabbar element. */
export function buildTabbar(
  availableIds: ReadonlySet<string>,
  badges: NavBadges = {},
): HTMLElement {
  const nav = document.createElement("nav");
  nav.className = "cust-tabbar";
  nav.setAttribute("aria-label", "Portal sections");
  nav.setAttribute("role", "tablist");

  nav.innerHTML = NAV_TABS
    .filter((t) => availableIds.has(t.id))
    .map(
      (t) => `
        <button type="button"
                class="cust-tab"
                role="tab"
                aria-selected="false"
                data-page="${t.id}">
          <svg viewBox="0 0 24 24" aria-hidden="true">${t.icon}</svg>
          <span class="cust-tab-label">${t.label}</span>
          ${badgeHtml(badges[t.id])}
        </button>
      `,
    )
    .join("");

  return nav;
}

/** Toggle which tab in `tabbar` carries `.cust-tab--active`. */
export function setActiveTab(tabbar: HTMLElement, page: string): void {
  const buttons = tabbar.querySelectorAll<HTMLButtonElement>(".cust-tab");
  buttons.forEach((btn) => {
    const active = btn.dataset["page"] === page;
    btn.classList.toggle("cust-tab--active", active);
    btn.setAttribute("aria-selected", active ? "true" : "false");
  });
}

// ── Mobile drawer ───────────────────────────────────────────────────────────

/** Build the hamburger button. Placed inside the header by the shell.
 *  Hidden via CSS above the desktop breakpoint. */
export function buildHamburger(): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "cust-header-iconbtn cust-nav-trigger";
  btn.setAttribute("aria-label", "Open navigation menu");
  btn.setAttribute("aria-expanded", "false");
  btn.setAttribute("aria-controls", "cust-nav-drawer");
  btn.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <line x1="3" y1="6"  x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  `;
  return btn;
}

/** Build the full-width mobile nav drawer. Hidden by default; the shell
 *  toggles `.cust-nav-drawer--open` to slide it in. */
export function buildMobileDrawer(
  availableIds: ReadonlySet<string>,
  badges: NavBadges = {},
): HTMLElement {
  const drawer = document.createElement("nav");
  drawer.id = "cust-nav-drawer";
  drawer.className = "cust-nav-drawer";
  drawer.setAttribute("aria-label", "Portal sections");
  drawer.setAttribute("role", "menu");
  drawer.setAttribute("aria-hidden", "true");

  drawer.innerHTML = `
    <ul class="cust-nav-drawer-list" role="none">
      ${NAV_TABS
        .filter((t) => availableIds.has(t.id))
        .map(
          (t) => `
            <li role="none">
              <button type="button"
                      class="cust-nav-drawer-item"
                      role="menuitem"
                      data-page="${t.id}">
                <span class="cust-nav-drawer-item-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true">${t.icon}</svg>
                </span>
                <span class="cust-nav-drawer-item-label">${t.label}</span>
                ${badgeHtml(badges[t.id])}
                <span class="cust-nav-drawer-item-chevron" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>
                </span>
              </button>
            </li>
          `,
        )
        .join("")}
    </ul>
  `;
  return drawer;
}

/** Build the backdrop that covers the page behind the drawer.
 *  Click → closes the drawer (wired by the shell). */
export function buildDrawerBackdrop(): HTMLElement {
  const el = document.createElement("div");
  el.className = "cust-nav-backdrop";
  el.setAttribute("aria-hidden", "true");
  return el;
}

/** Mark which drawer item carries `.cust-nav-drawer-item--active`. */
export function setActiveDrawerItem(drawer: HTMLElement, page: string): void {
  const items = drawer.querySelectorAll<HTMLButtonElement>(
    ".cust-nav-drawer-item",
  );
  items.forEach((btn) => {
    const active = btn.dataset["page"] === page;
    btn.classList.toggle("cust-nav-drawer-item--active", active);
    if (active) btn.setAttribute("aria-current", "page");
    else btn.removeAttribute("aria-current");
  });
}
